import React, { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import BackgroundPattern from './BackgroundPattern';
import { useReducedMotion } from '../hooks/useReducedMotion';

gsap.registerPlugin(ScrollTrigger);

const categories = [
  {
    number: '01',
    name: 'Creative Robot',
    desc: 'Design and build innovative robots with unique functionalities and creative designs.',
  },
  {
    number: '02',
    name: 'Capstone Project',
    desc: 'Showcase your final-year or major project integrating robotics and technology solutions.',
  },
  {
    number: '03',
    name: 'Autonomous Systems',
    desc: 'Develop self-navigating robots capable of complex tasks without human intervention.',
  },
  {
    number: '04',
    name: 'AI Innovation',
    desc: 'Combine artificial intelligence with robotics to create intelligent, adaptive systems.',
  },
];

const timeline = [
  { number: '01', event: 'Registration', desc: 'Start building your team and preparing your proposal.' },
  { number: '02', event: 'Submission', desc: 'Submit your initial proposal and design concept.' },
  { number: '03', event: 'Evaluation', desc: 'Top teams advance to the final round.' },
  { number: '04', event: 'Final Competition', desc: 'Present your robot at the grand event.' },
];

const Competition = () => {
  const sectionRef = useRef(null);
  const categoriesRef = useRef(null);
  const timelineRef = useRef(null);
  const progressLineRef = useRef(null);
  const imageRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) return;

    const ctx = gsap.context(() => {
      // Categories — cards enter from small vertical offset
      gsap.utils.toArray('.category-item').forEach((item, i) => {
        gsap.fromTo(
          item,
          { opacity: 0, y: 20 },
          {
            opacity: 1,
            y: 0,
            duration: 0.5,
            ease: 'power2.out',
            delay: i * 0.1,
            scrollTrigger: {
              trigger: categoriesRef.current,
              start: 'top 75%',
            },
          }
        );
      });

      // Timeline — progress line grows as user scrolls
      gsap.fromTo(
        progressLineRef.current,
        { scaleX: 0 },
        {
          scaleX: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: timelineRef.current,
            start: 'top 70%',
            end: 'bottom 60%',
            scrub: 0.5,
          },
        }
      );

      // Timeline items reveal progressively
      gsap.utils.toArray('.timeline-item').forEach((item, i) => {
        gsap.fromTo(
          item,
          { opacity: 0, x: -20 },
          {
            opacity: 1,
            x: 0,
            duration: 0.5,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: item,
              start: 'top 80%',
            },
          }
        );
      });

      // Image parallax
      gsap.fromTo(
        imageRef.current,
        { y: -15 },
        {
          y: 15,
          ease: 'none',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 0.5,
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, [prefersReducedMotion]);

  return (
    <section
      id="competition"
      ref={sectionRef}
      className="section-padding bg-surface-secondary dark:bg-dark-surface-secondary relative overflow-hidden"
    >
      <BackgroundPattern />
      <div className="section-container relative z-10">
        {/* Header — left aligned, editorial with image */}
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-16 lg:mb-20">
          <div className="lg:col-span-7">
            <span className="tech-label text-accent dark:text-dark-accent mb-6 block">
              COMPETITION / 2027
            </span>
            <h2 className="section-title mb-6">Four categories.</h2>
            <p className="section-desc">
              Designed to challenge and inspire the next generation of roboticists.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="relative overflow-hidden rounded-card border border-border dark:border-dark-border">
              <img
                ref={imageRef}
                src="https://images.unsplash.com/photo-1531746790731-6c087fecd65a?w=800&q=80"
                alt="Robotics engineering"
                className="w-full h-48 lg:h-56 object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              <div className="absolute bottom-4 left-4">
                <span className="tech-label text-white">SYSTEM / 04</span>
              </div>
            </div>
          </div>
        </div>

        {/* Categories — numbered editorial list */}
        <div ref={categoriesRef} className="mb-24">
          <div className="border-t border-border dark:border-dark-border">
            {categories.map((cat) => (
              <div
                key={cat.number}
                className="category-item group border-b border-border dark:border-dark-border py-8 lg:py-10 grid lg:grid-cols-12 gap-4 lg:gap-8 items-start hover:bg-surface dark:hover:bg-dark-surface transition-colors duration-200 px-4 lg:px-6 -mx-4 lg:-mx-6"
              >
                <div className="lg:col-span-2">
                  <span className="font-mono text-sm text-accent dark:text-dark-accent">
                    {cat.number}
                  </span>
                </div>
                <div className="lg:col-span-4">
                  <h3 className="text-2xl lg:text-3xl font-semibold text-text-primary dark:text-dark-text-primary tracking-[-0.02em]">
                    {cat.name}
                  </h3>
                </div>
                <div className="lg:col-span-5">
                  <p className="text-text-secondary dark:text-dark-text-secondary leading-relaxed">
                    {cat.desc}
                  </p>
                </div>
                <div className="lg:col-span-1 flex justify-end items-start">
                  <span className="text-text-muted dark:text-dark-text-muted group-hover:text-accent dark:group-hover:text-dark-accent transition-colors duration-200">
                    →
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline — horizontal progression */}
        <div ref={timelineRef} className="mb-24">
          <span className="tech-label text-accent dark:text-dark-accent mb-8 block">
            TIMELINE / PHASES
          </span>

          <div className="relative">
            {/* Progress line */}
            <div className="absolute left-0 right-0 top-4 h-px bg-border dark:bg-dark-border" />
            <div
              ref={progressLineRef}
              className="absolute left-0 top-4 h-px bg-accent dark:bg-dark-accent origin-left w-full"
              style={{ transform: 'scaleX(0)' }}
            />

            <div className="grid md:grid-cols-4 gap-8">
              {timeline.map((item) => (
                <div key={item.number} className="timeline-item relative pt-10">
                  <div className="absolute top-4 left-0 w-2 h-2 rounded-full bg-accent dark:bg-dark-accent -translate-y-1/2" />
                  <span className="font-mono text-xs text-accent dark:text-dark-accent">
                    {item.number}
                  </span>
                  <h4 className="text-lg font-semibold text-text-primary dark:text-dark-text-primary mt-2 mb-2">
                    {item.event}
                  </h4>
                  <p className="text-sm text-text-secondary dark:text-dark-text-secondary leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Requirements — structured list */}
        <div className="border border-border dark:border-dark-border rounded-card p-8 lg:p-10 bg-surface dark:bg-dark-surface">
          <span className="tech-label text-accent dark:text-dark-accent mb-6 block">
            REQUIREMENTS / ELIGIBILITY
          </span>
          <div className="grid sm:grid-cols-2 gap-6">
            {[
              'Teams of 2-5 members (individual participation allowed in junior category)',
              'Original work — no plagiarism allowed',
              'Technical documentation and video demonstration required',
              'Registration fee: $50 per team (waivers available)',
            ].map((req, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="font-mono text-xs text-accent dark:text-dark-accent mt-1 flex-shrink-0">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <p className="text-sm text-text-secondary dark:text-dark-text-secondary leading-relaxed">
                  {req}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Competition;